import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import type { Notification } from '../types/api';

const KIND_LABEL: Record<string, string> = {
    recommend:    'liked your post',
    comment:      'commented on your post',
    reply:        'replied to your comment',
    mention:      'mentioned you',
    message:      'sent you a message',
    review_nudge: 'looks like you visited — leave a review?',
};

interface NewsroomAlert {
    community_id:   string;
    community_slug: string;
    community_name: string;
    pending_count:  number;
    oldest_ago?:    string;
}
interface PausedAgentAlert {
    community_id:   string;
    community_slug: string;
    community_name: string;
    agent_id:       string;
    agent_name:     string;
    agent_role:     string;
    paused_reason:  string;
}

/** Bell + dropdown. Live-updates via SSE, poll fallback every 60s.
 * Also folds in newsroom alerts (pending queue counts) for every
 * community the caller moderates. */
export function Bell() {
    const api = useApi();
    const { token } = useAuth();
    const [unread, setUnread] = useState(0);
    const [alerts, setAlerts] = useState<NewsroomAlert[]>([]);
    const [pausedAgents, setPausedAgents] = useState<PausedAgentAlert[]>([]);
    const [open,   setOpen]   = useState(false);
    const [items,  setItems]  = useState<Notification[] | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Combined badge count: unread notifications + pending queue items
    // + paused agents across all my communities. Editor sees one number,
    // one bell. Paused agents count as 1 apiece — they need attention
    // even if they're not "pending" in the queue sense.
    const totalPending = alerts.reduce((n, a) => n + a.pending_count, 0);
    const badgeTotal = unread + totalPending + pausedAgents.length;

    const refreshBadge = useCallback(async () => {
        try {
            const [n, a] = await Promise.all([
                api.get<{ count: number }>('/api/notifications/unread-count'),
                api.get<{ alerts: NewsroomAlert[]; paused_agents?: PausedAgentAlert[] }>('/api/me/newsroom-alerts')
                    .catch(() => ({ alerts: [], paused_agents: [] })),
            ]);
            setUnread(n.count ?? 0);
            setAlerts(a.alerts ?? []);
            setPausedAgents(a.paused_agents ?? []);
        } catch { /* offline / not-yet-authed — harmless */ }
    }, [api]);

    // Poll fallback
    useEffect(() => {
        refreshBadge();
        const id = setInterval(refreshBadge, 60_000);
        return () => clearInterval(id);
    }, [refreshBadge]);

    // SSE
    useEffect(() => {
        const t = token();
        if (!t) return;
        const es = new EventSource('/api/notifications/stream?access_token=' + encodeURIComponent(t));
        const onNotif = () => {
            refreshBadge();
            if (open) loadItems();
        };
        es.addEventListener('notification', onNotif);
        es.onerror = () => { /* browser auto-reconnects; poll covers gaps */ };
        return () => { es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, refreshBadge, open]);

    // Close on outside click
    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (!open) return;
            if (wrapRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        }
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [open]);

    async function loadItems() {
        try {
            const r = await api.get<{ notifications: Notification[] }>('/api/notifications?limit=10');
            setItems(r.notifications ?? []);
        } catch { setItems([]); }
    }
    async function toggle() {
        if (open) { setOpen(false); return; }
        await loadItems();
        setOpen(true);
    }
    async function markRead(n: Notification) {
        await api.post(`/api/notifications/${n.id}/read`);
        setItems(items?.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x) ?? null);
        refreshBadge();
    }
    async function markAllRead() {
        await api.post('/api/notifications/read-all');
        setOpen(false);
        refreshBadge();
    }

    return (
        <div className="bell-wrap" ref={wrapRef}>
            <button
                type="button" className="topbar-btn" aria-label="Notifications" title="Notifications"
                onClick={toggle}
            >
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M4 15V9a6 6 0 1 1 12 0v6l1.5 2h-15L4 15Z" />
                    <path d="M8 17.5a2 2 0 0 0 4 0" />
                </svg>
                {badgeTotal > 0 && <span className="bell-badge">{badgeTotal}</span>}
            </button>
            {open && (
                <div className="bell-dropdown">
                    {alerts.length > 0 && (
                        <>
                            <div className="notif-head" style={{ background: '#faf3d9' }}>
                                <span>Editorial queue</span>
                                <span style={{ fontSize: 11, color: '#666' }}>{totalPending} pending</span>
                            </div>
                            {alerts.map(a => (
                                <a
                                    key={a.community_id}
                                    className="notif-item unread"
                                    href={`/communities/${a.community_slug}/newsroom`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpen(false);
                                        window.location.href = e.currentTarget.href;
                                    }}
                                >
                                    <span className="kind" style={{ color: '#8b1919' }}>
                                        {a.pending_count} pending in {a.community_name}
                                    </span>
                                    {a.oldest_ago && (
                                        <span className="when" style={{ marginLeft: 6 }}>
                                            oldest {a.oldest_ago}
                                        </span>
                                    )}
                                </a>
                            ))}
                        </>
                    )}
                    {pausedAgents.length > 0 && (
                        <>
                            <div className="notif-head" style={{ background: '#f5dcdc' }}>
                                <span>⚠ Agent errors</span>
                                <span style={{ fontSize: 11, color: '#666' }}>{pausedAgents.length} paused</span>
                            </div>
                            {pausedAgents.map(pa => (
                                <a
                                    key={pa.agent_id}
                                    className="notif-item unread"
                                    href="/admin/ai-agents"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpen(false);
                                        window.location.href = e.currentTarget.href;
                                    }}
                                    title={pa.paused_reason}
                                >
                                    <span className="kind" style={{ color: '#8b1919' }}>
                                        {pa.agent_name} ({pa.agent_role}) paused in {pa.community_name}
                                    </span>
                                    {pa.paused_reason && (
                                        <div className="snippet" style={{ color: '#666' }}>
                                            {pa.paused_reason.length > 90
                                                ? pa.paused_reason.slice(0, 90) + '…'
                                                : pa.paused_reason}
                                        </div>
                                    )}
                                </a>
                            ))}
                        </>
                    )}
                    <div className="notif-head">
                        <span>Notifications</span>
                        <button type="button" onClick={markAllRead}>Mark all read</button>
                    </div>
                    {(items ?? []).length === 0 ? (
                        <div className="notif-empty">No notifications yet.</div>
                    ) : items!.map(n => {
                        const snippet = typeof n.body?.snippet === 'string' ? n.body.snippet as string : '';
                        // "message" notifications route to /messages?with=<sender>
                        // (the actor is the sender); posts + comments to /?post=<id>.
                        const href = n.kind === 'message'
                            ? '/messages' + (n.actor_user_id ? '?with=' + n.actor_user_id : '')
                            : '/?post=' + n.subject_id;
                        return (
                            <a
                                key={n.id}
                                className={'notif-item' + (n.read_at ? '' : ' unread')}
                                href={href}
                                onClick={(e) => { e.preventDefault(); markRead(n); window.location.href = e.currentTarget.href; }}
                            >
                                <span className="kind">{KIND_LABEL[n.kind] ?? n.kind}</span>
                                <span className="when" style={{ marginLeft: 6 }}>{timeAgo(n.created_at)}</span>
                                {snippet && <div className="snippet">"{snippet}"</div>}
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
